const ALLOWED_IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg']);

/** JPEG may be reported as image/jpeg or image/pjpeg on some browsers/OS. */
const ALLOWED_IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/pjpeg']);

/** Shown when a file fails image type validation. */
export const IMAGE_UPLOAD_VALIDATION_MESSAGE =
  'Only JPG and PNG image formats are allowed.';

/** File picker accept list — extensions only (cleaner filter on Windows). */
export const IMAGE_UPLOAD_ACCEPT = '.png,.jpg,.jpeg';

const ALLOWED_VIDEO_EXTENSIONS = new Set([
  'mp4',
  'webm',
  'mov',
  'avi',
  'ogg',
  'mkv',
]);

const ALLOWED_VIDEO_MIME_TYPES = new Set([
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-msvideo',
  'video/ogg',
  'video/x-matroska',
]);

/** Ticket create/edit — images + PDF + common video formats from PC. */
export const TICKET_ATTACHMENT_UPLOAD_ACCEPT =
  '.png,.jpg,.jpeg,.pdf,.mp4,.webm,.mov,.avi,.ogg,.mkv';

const TICKET_ATTACHMENT_PICKER_TYPES: FilePickerAcceptType[] = [
  {
    description: 'JPG and PNG images',
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
  },
  {
    description: 'PDF documents',
    accept: {
      'application/pdf': ['.pdf'],
    },
  },
  {
    description: 'Videos',
    accept: {
      'video/mp4': ['.mp4'],
      'video/webm': ['.webm'],
      'video/quicktime': ['.mov'],
      'video/x-msvideo': ['.avi'],
      'video/ogg': ['.ogg'],
      'video/x-matroska': ['.mkv'],
    },
  },
];

const ALLOWED_DOCUMENT_EXTENSIONS = new Set(['pdf']);
const ALLOWED_DOCUMENT_MIME_TYPES = new Set(['application/pdf']);

export const TICKET_ATTACHMENT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const TICKET_ATTACHMENT_VIDEO_MAX_BYTES = 50 * 1024 * 1024;
export const TICKET_ATTACHMENT_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

export const TICKET_ATTACHMENT_UPLOAD_HINT =
  'Images: JPG or PNG only (max 5 MB). PDFs up to 10 MB. Videos from your PC: MP4, WebM, MOV, AVI, OGG, or MKV (max 50 MB each). You can attach multiple files.';

const IMAGE_PICKER_TYPES: FilePickerAcceptType[] = [
  {
    description: 'JPG and PNG images only',
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
  },
];

export function getFileExtension(name: string): string {
  const base = name.trim().split(/[/\\]/).pop() ?? '';
  const dot = base.lastIndexOf('.');
  return dot >= 0 ? base.slice(dot + 1).toLowerCase() : '';
}

const IMAGE_LIKE_EXTENSIONS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'avif', 'heic', 'heif',
]);

/** True when the file is treated as an image upload (not video/pdf/doc). */
export function isImageLikeUpload(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('image/')) {
    return true;
  }
  const ext = getFileExtension(file.name);
  return IMAGE_LIKE_EXTENSIONS.has(ext);
}

/** Returns false when an image-like file is not JPG/PNG (shows toast). */
export function validateImageUploadFile(file: File): boolean {
  if (!isImageLikeUpload(file)) {
    return true;
  }
  return isAllowedImageUpload(file);
}

/** True when schema explicitly allows PDF, video, documents, etc. */
export function schemaAllowsNonImageUploads(schemaAccept?: string): boolean {
  if (!schemaAccept?.trim()) {
    return false;
  }
  const a = schemaAccept.toLowerCase();
  return (
    a.includes('pdf') ||
    a.includes('video') ||
    a.includes('application/') ||
    a.includes('.pdf') ||
    a.includes('.webm') ||
    a.includes('.mp4') ||
    a.includes('.doc') ||
    a.includes('.zip')
  );
}

/** Maps schema `accept="image/*"` (etc.) to JPG/PNG-only picker list. */
export function resolveImageFieldAccept(schemaAccept?: string): string | undefined {
  if (!schemaAccept?.trim()) {
    return undefined;
  }
  const a = schemaAccept.toLowerCase();
  if (
    a === 'image/*' ||
    a.includes('image/png') ||
    a.includes('image/jpeg') ||
    /\.(png|jpe?g|jpg)/.test(a)
  ) {
    return IMAGE_UPLOAD_ACCEPT;
  }
  return schemaAccept;
}

export function isAllowedImageUpload(file: File): boolean {
  const ext = getFileExtension(file.name);
  const type = (file.type || '').toLowerCase();

  if (ext && !ALLOWED_IMAGE_EXTENSIONS.has(ext)) {
    return false;
  }

  if (type) {
    if (!type.startsWith('image/')) {
      return false;
    }
    if (!ALLOWED_IMAGE_MIME_TYPES.has(type)) {
      return false;
    }
  }

  if (ext) {
    return ALLOWED_IMAGE_EXTENSIONS.has(ext);
  }

  return ALLOWED_IMAGE_MIME_TYPES.has(type);
}

export function isAllowedVideoUpload(file: File): boolean {
  const ext = getFileExtension(file.name);
  const type = (file.type || '').toLowerCase();

  if (ext && ALLOWED_VIDEO_EXTENSIONS.has(ext)) {
    if (type && !type.startsWith('video/')) {
      return ALLOWED_VIDEO_MIME_TYPES.has(type);
    }
    return true;
  }

  if (type.startsWith('video/')) {
    return ALLOWED_VIDEO_MIME_TYPES.has(type);
  }

  return false;
}

export function isAllowedDocumentUpload(file: File): boolean {
  const ext = getFileExtension(file.name);
  const type = (file.type || '').toLowerCase();

  if (ext && ALLOWED_DOCUMENT_EXTENSIONS.has(ext)) {
    return true;
  }

  return type ? ALLOWED_DOCUMENT_MIME_TYPES.has(type) : false;
}

export function isAllowedTicketAttachmentUpload(file: File): boolean {
  return (
    isAllowedImageUpload(file) ||
    isAllowedVideoUpload(file) ||
    isAllowedDocumentUpload(file)
  );
}

export function isImageDataUrl(dataUrl: string): boolean {
  return /^data:image\/(png|jpe?g);/i.test(dataUrl);
}

/** Shown under image-only upload areas. */
export const IMAGE_UPLOAD_HINT = `${IMAGE_UPLOAD_VALIDATION_MESSAGE} Max 5 MB per file.`;

/** Shown on mixed file upload fields (images + PDF/video, etc.). */
export const IMAGE_UPLOAD_HINT_MIXED =
  'Image files must be JPG or PNG only. Other attachment types are not restricted.';

/**
 * Opens a file picker restricted to JPG/PNG.
 * Uses showOpenFilePicker on Chromium/Edge (strict filter); falls back to a hidden file input.
 */
export async function pickImageUploadFiles(
  multiple = true,
  fallbackInput?: HTMLInputElement | null
): Promise<File[]> {
  const w = window as Window & {
    showOpenFilePicker?: (
      options?: OpenFilePickerOptions
    ) => Promise<FileSystemFileHandle[]>;
  };

  if (typeof w.showOpenFilePicker === 'function') {
    try {
      const handles = await w.showOpenFilePicker({
        multiple,
        types: IMAGE_PICKER_TYPES,
      });
      return Promise.all(handles.map(h => h.getFile()));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return [];
      }
    }
  }

  return new Promise(resolve => {
    const input = fallbackInput ?? document.createElement('input');
    const owned = !fallbackInput;
    if (owned) {
      input.type = 'file';
      input.style.display = 'none';
      document.body.appendChild(input);
    }
    input.accept = IMAGE_UPLOAD_ACCEPT;
    input.multiple = multiple;
    input.value = '';

    const onChange = () => {
      resolve(Array.from(input.files ?? []));
      input.removeEventListener('change', onChange);
      input.value = '';
      if (owned) {
        input.remove();
      }
    };
    input.addEventListener('change', onChange, { once: true });
    input.click();
  });
}

/** Toast title when validation fails. */
export const IMAGE_UPLOAD_ERROR_TITLE = 'Invalid attachment';

export function imageUploadRejectedMessage(_fileName?: string): string {
  return IMAGE_UPLOAD_VALIDATION_MESSAGE;
}

export function ticketAttachmentRejectedMessage(file?: File): string {
  if (file && isAllowedVideoUpload(file)) {
    return 'Only MP4, WebM, MOV, AVI, OGG, and MKV video formats are allowed.';
  }
  if (file && !isImageLikeUpload(file) && !isAllowedVideoUpload(file)) {
    return TICKET_ATTACHMENT_UPLOAD_HINT;
  }
  return IMAGE_UPLOAD_VALIDATION_MESSAGE;
}

/**
 * Opens a file picker for ticket attachments (JPG/PNG images + videos).
 */
export async function pickTicketAttachmentUploadFiles(
  multiple = true,
  fallbackInput?: HTMLInputElement | null
): Promise<File[]> {
  const w = window as Window & {
    showOpenFilePicker?: (
      options?: OpenFilePickerOptions
    ) => Promise<FileSystemFileHandle[]>;
  };

  if (typeof w.showOpenFilePicker === 'function') {
    try {
      const handles = await w.showOpenFilePicker({
        multiple,
        types: TICKET_ATTACHMENT_PICKER_TYPES,
      });
      return Promise.all(handles.map(h => h.getFile()));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return [];
      }
    }
  }

  return new Promise(resolve => {
    const input = fallbackInput ?? document.createElement('input');
    const owned = !fallbackInput;
    if (owned) {
      input.type = 'file';
      input.style.display = 'none';
      document.body.appendChild(input);
    }
    input.accept = TICKET_ATTACHMENT_UPLOAD_ACCEPT;
    input.multiple = multiple;
    input.value = '';

    const onChange = () => {
      resolve(Array.from(input.files ?? []));
      input.removeEventListener('change', onChange);
      input.value = '';
      if (owned) {
        input.remove();
      }
    };
    input.addEventListener('change', onChange, { once: true });
    input.click();
  });
}
